import { useState, useMemo, useEffect } from "react";
import { useModels } from "../hooks/useApi";
import Card from "../components/Card";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import ModelDetailDrawer from "../components/ModelDetailDrawer";

const inputClass =
  "h-9 px-3 rounded-btn border border-line text-small text-text-secondary bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30";

const MODEL_TYPES = [
  { value: "", label: "همه انواع" },
  { value: "text", label: "متنی" },
  { value: "multimodal", label: "چندوجهی" },
  { value: "image", label: "تصویر" },
  { value: "audio", label: "صوتی" },
  { value: "video", label: "ویدئو" },
  { value: "embedding", label: "Embedding" },
];

const TYPE_LABELS: Record<string, string> = {
  text: "متنی",
  multimodal: "چندوجهی",
  image: "تصویر",
  audio: "صوتی",
  video: "ویدئو",
  embedding: "Embedding",
};

function typeBadge(type: string) {
  return <Badge variant="info">{TYPE_LABELS[type] || type}</Badge>;
}

function unitSuffix(basis: string, unitType: string): string {
  if (basis === "per_second") return "ثانیه";
  if (basis === "per_image") return "تصویر";
  if (unitType === "characters") return "کاراکتر";
  return "توکن";
}

function cutDecimals(value: number, decimals: number): number {
  const [intPart, decPart = ""] = (value + 1e-12).toFixed(15).split(".");
  return parseFloat(`${intPart}.${decPart.slice(0, decimals)}`);
}

function formatUsd(value: number | null | undefined): string {
  if (value == null) return "—";
  const v = Math.abs(value);
  if (v === 0) return "$0";
  if (v < 0.01) return `$${cutDecimals(v, 9).toFixed(9).replace(/0+$/, "")}`;
  if (v < 100) return `$${cutDecimals(v, 2).toFixed(2)}`;
  return `$${Math.trunc(v).toLocaleString("en-US")}`;
}

function applyMargin(value: number | null | undefined, feePercent: number | null | undefined): number | null {
  if (value == null) return null;
  return (value * (100 + (feePercent ?? 0))) / 100;
}

function priceCell(value: number | null | undefined, basis: string, unitType: string) {
  if (value == null) {
    return <span className="text-text-tertiary">—</span>;
  }
  const isPerMillion = basis === "per_1m";
  return (
    <span className="font-mono font-semibold text-left" dir="ltr">
      {formatUsd(value)}
      <span className="text-small font-normal text-text-tertiary mr-1">
        /{isPerMillion ? "1M" : ""}
        {unitSuffix(basis, unitType)}
      </span>
    </span>
  );
}

export default function Models() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [modelType, setModelType] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [selectedModel, setSelectedModel] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const params = useMemo(() => {
    const p: Record<string, unknown> = { search: debouncedSearch };
    if (modelType) p.model_type = modelType;
    const sortMap: Record<string, { sort: string; order: string }> = {
      price_asc: { sort: "price", order: "asc" },
      price_desc: { sort: "price", order: "desc" },
      name_asc: { sort: "model_name", order: "asc" },
      name_desc: { sort: "model_name", order: "desc" },
    };
    const s = sortMap[sortOrder];
    if (s) {
      p.sort = s.sort;
      p.order = s.order;
    }
    return p;
  }, [debouncedSearch, modelType, sortOrder]);

  const hasFilters = Boolean(debouncedSearch || modelType || sortOrder);

  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setModelType("");
    setSortOrder("");
  };

  const { data: response, isLoading } = useModels(params);
  const models = (response as { items?: Record<string, unknown>[]; total_count?: number })?.items;
  const totalCount = (response as { items?: Record<string, unknown>[]; total_count?: number })?.total_count;

  const columns = [
    {
      key: "model_name",
      header: "مدل",
      render: (m: Record<string, unknown>) => (
        <div className="flex flex-col">
          <span className="font-mono font-semibold text-left ltr block">{m.model_name as string}</span>
          {(m.display_name as string) && (
            <span className="text-small text-text-tertiary mt-0.5">{m.display_name as string}</span>
          )}
        </div>
      ),
    },
    {
      key: "provider",
      header: "تأمین‌کننده",
      render: (m: Record<string, unknown>) => (
        <span className="text-body text-text-secondary ltr block text-left">{m.provider as string || "—"}</span>
      ),
    },
    {
      key: "model_type",
      header: "نوع",
      render: (m: Record<string, unknown>) => typeBadge((m.model_type as string) || "text"),
    },
    {
      key: "input_price_usd",
      header: "هزینه ورودی",
      render: (m: Record<string, unknown>) =>
        priceCell(
          applyMargin(m.input_price_usd as number | null, m.platform_fee_percent as number | null),
          (m.price_basis as string) || "per_1m",
          (m.unit_type as string) || "tokens"
        ),
    },
    {
      key: "output_price_usd",
      header: "هزینه خروجی",
      render: (m: Record<string, unknown>) =>
        priceCell(
          applyMargin(m.output_price_usd as number | null, m.platform_fee_percent as number | null),
          (m.price_basis as string) || "per_1m",
          (m.unit_type as string) || "tokens"
        ),
    },
    {
      key: "context_length",
      header: "پنجره کانتکست",
      render: (m: Record<string, unknown>) =>
        m.context_length != null ? (
          <span className="font-mono font-semibold text-left" dir="ltr">
            {Number(m.context_length).toLocaleString("en-US")}
          </span>
        ) : (
          <span className="text-text-tertiary">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-h1 text-text-main">مدل‌ها</h1>
          <p className="text-body text-text-secondary mt-1">مدل‌های هوش مصنوعی در دسترس و قیمت‌گذاری</p>
        </div>
      </div>

      {/* Toolbar: search + type filter + sort */}
      <Card padding="md">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2"
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="#8E9AAA" strokeWidth="2" strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="جستجوی مدل..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${inputClass} w-full pr-9`}
            />
          </div>

          <select
            value={modelType}
            onChange={(e) => setModelType(e.target.value)}
            className={inputClass}
            style={{ width: 140 }}
          >
            {MODEL_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className={inputClass}
            style={{ width: 190 }}
          >
            <option value="">مرتب‌سازی</option>
            <option value="price_asc">قیمت: کم به زیاد</option>
            <option value="price_desc">قیمت: زیاد به کم</option>
            <option value="name_asc">نام: A تا Z</option>
            <option value="name_desc">نام: Z تا A</option>
          </select>

          {hasFilters && (
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-btn text-small font-medium text-text-secondary border border-line bg-surface hover:bg-surface-muted"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
              بازنشانی فیلترها
            </button>
          )}

          <span className="text-small text-text-tertiary mr-auto">
            {totalCount != null ? `${totalCount.toLocaleString("en-US")} مدل` : ""}
          </span>
        </div>
      </Card>

      {/* Models Table */}
      <Card padding="md">
        <DataTable
          columns={columns}
          data={(models || []) as Record<string, unknown>[]}
          loading={isLoading}
          emptyMessage="هیچ مدلی یافت نشد"
          onRowClick={(item) => setSelectedModel(item)}
        />
      </Card>

      <ModelDetailDrawer selectedModel={selectedModel} onClose={() => setSelectedModel(null)} />
    </div>
  );
}
